import { Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { isEmpty } from 'lodash';
import React, { MouseEvent, useContext, useEffect, useMemo, useState } from 'react';
import { H } from '../Layout';
import { useStyling } from '../Theme';
import { UserContext } from '../auth/UserContext';
import { useI18n } from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import type { SavePreferenceFormData } from '../types/savePreference';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import type { GridSettingsPreference } from './DataGrid/gridSettings/types';
import {
    type ColumnVisibilityPreset,
    buildAllColumnsFavouriteModel,
    findOwnFavouritePreference,
    getColumnPresetLabel,
    getFavouritePreferenceModel,
    getFavouritePresetLabel
} from './ColumnVisibiltyUtills';
import { SavedColumnViewsChrome, SavedColumnViewsMenuItems } from './SavedColumnViewsControls';
import type { ColumnViewSelection, FavouriteColumnPresetModel, SavedColumnView } from './columnViewTypes';
import {
    ALL_COLUMNS,
    DEFAULT_COLUMNS,
    FAVOURITE_COLUMN_PRESET_KEY,
    MY_COLUMNS,
    PRESET_INDEX
} from './columnVisibilitySelectorConstants';

export type { ColumnViewSelection } from './columnViewTypes';

type ColumnVisibilitySelectorProps = {
    id: string;
    labelToken?: string;
    defaultColumnVisibility: GridColumnVisibilityModel;
    customColumnVisibility: GridColumnVisibilityModel;
    presets: ColumnVisibilityPreset[];
    selectedPreset: number;
    selectedSavedViewId?: string | null;
    onPresetSelected: (index: number, selection: ColumnViewSelection) => void;
    enabledFavouritePreset: boolean;
    hideMyColumnsPreset?: boolean;
    hideAllColumnsPreset?: boolean;
    hideDefaultColumnsPreset?: boolean;
    enableSaveColumnView?: boolean;
    savedColumnViews?: SavedColumnView[];
    onSaveColumnView?: (formData: SavePreferenceFormData) => Promise<SavedColumnView | null>;
    onUpdateColumnView?: () => Promise<SavedColumnView | null>;
    onDeleteColumnView?: (viewId: string) => Promise<boolean>;
    savingColumnView?: boolean;
    deletingColumnView?: boolean;
    hasUnsavedColumnViewChanges?: boolean;
};

export function ColumnVisibilitySelector({
    id,
    labelToken,
    defaultColumnVisibility,
    customColumnVisibility,
    presets,
    selectedPreset,
    selectedSavedViewId = null,
    onPresetSelected,
    enabledFavouritePreset,
    hideMyColumnsPreset,
    hideAllColumnsPreset,
    hideDefaultColumnsPreset,
    enableSaveColumnView = false,
    savedColumnViews = [],
    onSaveColumnView,
    onUpdateColumnView,
    onDeleteColumnView,
    savingColumnView = false,
    deletingColumnView = false,
    hasUnsavedColumnViewChanges = false
}: ColumnVisibilitySelectorProps) {
    const t = useI18n();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const { theme, isMobile } = useStyling();
    const [starredLabel, setStarredLabel] = useState('');
    const { preferences, savePreference, preferencesLoaded, userProfile, application } = useContext(UserContext);
    const [savingFavourite, setSavingFavourite] = useState(false);
    const [viewPendingDelete, setViewPendingDelete] = useState<SavedColumnView | null>(null);
    const finalLabelToken = labelToken ?? 'grid.column.presets.label';
    const accountId = userProfile?.accountId;
    const applicationName = application?.name;

    // Star UI only — visibility/order hydrate lives in useColumnVisibility to avoid double apply.
    // Sync when the prefs-ready flag or favourite identity (grid/account/app) changes.
    // Intentionally omit `preferences`: mid-save preference updates must not fight optimistic star clicks.
    useEffect(() => {
        if (!preferencesLoaded || !enabledFavouritePreset) {
            setStarredLabel('');
            return;
        }
        const favourite = findOwnFavouritePreference(preferences, id, accountId, applicationName);
        const parsed = getFavouritePreferenceModel(favourite);
        const label = getFavouritePresetLabel(favourite, parsed);
        setStarredLabel(favourite && parsed && label ? label : '');
    }, [preferencesLoaded, enabledFavouritePreset, id, accountId, applicationName]);

    const selectedSavedView = useMemo(
        () => savedColumnViews.find((view) => view.id === selectedSavedViewId) ?? null,
        [savedColumnViews, selectedSavedViewId]
    );

    function handleOpenMenu(event: MouseEvent<HTMLButtonElement>) {
        setAnchorEl(event.currentTarget);
    }

    function handleCloseMenu() {
        setAnchorEl(null);
    }

    function getPresetLabel() {
        return getColumnPresetLabel(selectedPreset, presets, t, selectedSavedView?.name);
    }

    function isFavouriteAlreadySelected(index: number, savedViewId?: string) {
        if (selectedPreset !== index) {
            return false;
        }
        if (index === PRESET_INDEX.saved) {
            return selectedSavedViewId === savedViewId;
        }
        return true;
    }

    async function handleIconClick(
        e: MouseEvent,
        label: string,
        model: GridColumnVisibilityModel,
        index: number,
        orderedFields?: string[],
        savedViewId?: string,
        gridSettings?: GridSettingsPreference
    ) {
        e.stopPropagation();

        setSavingFavourite(true);
        const previousLabel = starredLabel;
        const newModel: FavouriteColumnPresetModel = {
            ...model,
            presetKey: label,
            currentFavourite: true,
            currentIndex: index,
            ...(orderedFields ? { orderedFields } : {}),
            ...(savedViewId ? { savedViewId } : {})
        };
        setStarredLabel(label);
        // Starring the view that is already open only stores the star.
        // Re-applying it drops saved sort and group and marks the view dirty.
        if (!isFavouriteAlreadySelected(index, savedViewId)) {
            onPresetSelected(index, {
                visibilityModel: newModel,
                orderedFields,
                savedViewId,
                gridSettings
            });
        }

        try {
            await savePreference(id, FAVOURITE_COLUMN_PRESET_KEY, JSON.stringify(newModel));
            successToastMessage('grid.preference.saved.text');
        } catch {
            failedToastMessage('grid.preference.failedToSave.text');
            const favourite = findOwnFavouritePreference(preferences, id, accountId, applicationName);
            const parsed = getFavouritePreferenceModel(favourite);
            const persistedLabel = getFavouritePresetLabel(favourite, parsed);
            setStarredLabel(favourite && parsed && persistedLabel ? persistedLabel : previousLabel);
        } finally {
            setSavingFavourite(false);
        }
    }

    async function handleFavouriteViewDeleted(deletedView: SavedColumnView) {
        const favourite = findOwnFavouritePreference(preferences, id, accountId, applicationName);
        const parsed = getFavouritePreferenceModel(favourite);
        const wasFavourited =
            starredLabel === deletedView.name ||
            (typeof parsed?.savedViewId === 'string' && parsed.savedViewId === deletedView.id);
        if (!wasFavourited) {
            return;
        }

        setStarredLabel(ALL_COLUMNS);
        try {
            await savePreference(id, FAVOURITE_COLUMN_PRESET_KEY, JSON.stringify(buildAllColumnsFavouriteModel()));
        } catch {
            failedToastMessage('grid.preference.failedToSave.text');
        }
    }

    function renderFavouriteStar(
        label: string,
        model: GridColumnVisibilityModel,
        index: number,
        orderedFields?: string[],
        savedViewId?: string,
        gridSettings?: GridSettingsPreference
    ) {
        if (!enabledFavouritePreset) {
            return null;
        }
        return (
            <Tooltip title={t('grid.presets.favourite.text')}>
                <IconButton
                    size="small"
                    disabled={savingFavourite}
                    onClick={(e) => handleIconClick(e, label, model, index, orderedFields, savedViewId, gridSettings)}
                >
                    {starredLabel === label ? <Icons.Star color="primary" /> : <Icons.StarOutline color="disabled" />}
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <H className="_presetsMenu" gap={1} sx={{ width: 'unset', alignItems: 'center' }}>
            {!isMobile ? (
                <Button
                    sx={{
                        whiteSpace: 'nowrap',
                        color: theme.palette.primary.main
                    }}
                    onClick={handleOpenMenu}
                    variant={'text'}
                    size="small"
                    startIcon={<Icons.ListViewTable />}
                >
                    <H gap={0.5}>
                        {t(finalLabelToken)} ({getPresetLabel()})
                    </H>
                </Button>
            ) : (
                <IconButton
                    sx={{
                        color: theme.palette.primary.main,
                        px: 0.5,
                        py: 0
                    }}
                    onClick={handleOpenMenu}
                >
                    <Icons.Tune />
                </IconButton>
            )}

            {enableSaveColumnView && onSaveColumnView && (
                <SavedColumnViewsChrome
                    savedColumnViews={savedColumnViews}
                    selectedSavedViewId={selectedSavedViewId}
                    selectedPreset={selectedPreset}
                    hasUnsavedColumnViewChanges={hasUnsavedColumnViewChanges}
                    savingColumnView={savingColumnView}
                    deletingColumnView={deletingColumnView}
                    onSaveColumnView={onSaveColumnView}
                    onUpdateColumnView={onUpdateColumnView}
                    onDeleteColumnView={onDeleteColumnView}
                    onPresetSelected={onPresetSelected}
                    onFavouriteViewDeleted={handleFavouriteViewDeleted}
                    viewPendingDelete={viewPendingDelete}
                    onViewPendingDeleteChange={setViewPendingDelete}
                />
            )}

            {anchorEl && (
                <Menu
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                    }}
                    id={'col-visibility-menu'}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    open={true}
                    onClose={handleCloseMenu}
                >
                    {!hideAllColumnsPreset && (
                        <MenuItem
                            key={'all'}
                            selected={selectedPreset === PRESET_INDEX.all}
                            onClick={() => {
                                onPresetSelected(PRESET_INDEX.all, { visibilityModel: {} });
                                handleCloseMenu();
                            }}
                        >
                            <ListItemIcon>{<Icons.AllColumns />}</ListItemIcon>
                            <ListItemText>{t('grid.column.presets.all')}</ListItemText>
                            {renderFavouriteStar(ALL_COLUMNS, {}, PRESET_INDEX.all)}
                        </MenuItem>
                    )}
                    {!hideDefaultColumnsPreset && !isEmpty(defaultColumnVisibility) && (
                        <MenuItem
                            key={'default'}
                            selected={selectedPreset === PRESET_INDEX.default}
                            onClick={() => {
                                onPresetSelected(PRESET_INDEX.default, {
                                    visibilityModel: defaultColumnVisibility
                                });
                                handleCloseMenu();
                            }}
                        >
                            <ListItemIcon>{<Icons.DefaultColumns />}</ListItemIcon>
                            <ListItemText>{t('grid.column.presets.default')}</ListItemText>
                            {renderFavouriteStar(DEFAULT_COLUMNS, defaultColumnVisibility, PRESET_INDEX.default)}
                        </MenuItem>
                    )}
                    {presets?.map((preset: ColumnVisibilityPreset, index) => {
                        const model: GridColumnVisibilityModel = {};
                        preset.hidden.forEach((colName) => {
                            model[colName] = false;
                        });
                        return (
                            <MenuItem
                                key={index}
                                selected={selectedPreset === index}
                                onClick={() => {
                                    onPresetSelected(index, { visibilityModel: model });
                                    handleCloseMenu();
                                }}
                            >
                                <ListItemIcon>{preset.icon ? <preset.icon /> : null}</ListItemIcon>
                                <ListItemText>{preset.label}</ListItemText>
                                {renderFavouriteStar(preset.label, model, index)}
                            </MenuItem>
                        );
                    })}

                    {enableSaveColumnView && (
                        <SavedColumnViewsMenuItems
                            savedColumnViews={savedColumnViews}
                            selectedSavedViewId={selectedSavedViewId}
                            selectedPreset={selectedPreset}
                            deletingColumnView={deletingColumnView}
                            onDeleteColumnView={onDeleteColumnView}
                            onPresetSelected={onPresetSelected}
                            onCloseMenu={handleCloseMenu}
                            onRequestDelete={setViewPendingDelete}
                            renderFavouriteStar={renderFavouriteStar}
                        />
                    )}

                    {!hideMyColumnsPreset && customColumnVisibility && (
                        <MenuItem
                            key={'custom'}
                            selected={selectedPreset === PRESET_INDEX.my}
                            onClick={() => {
                                onPresetSelected(PRESET_INDEX.my, {
                                    visibilityModel: customColumnVisibility
                                });
                                handleCloseMenu();
                            }}
                        >
                            <ListItemIcon>{<Icons.Star />}</ListItemIcon>
                            <ListItemText>{t('grid.column.presets.my')}</ListItemText>
                            {renderFavouriteStar(MY_COLUMNS, customColumnVisibility, PRESET_INDEX.my)}
                        </MenuItem>
                    )}
                </Menu>
            )}
        </H>
    );
}
